import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import "./BillPopup.css";
import { Api } from "../Api/Api";

const TAX_OPTIONS = [
  { label: "CGST 3% + SGST 3%", cgst: 3, sgst: 3 },
  { label: "CGST 6% + SGST 6%", cgst: 6, sgst: 6 },
  { label: "CGST 9% + SGST 9%", cgst: 9, sgst: 9 },
];

export default function BillPopup({
  open,
  onClose,
  header,
  show,
  onHide,
  company,
  buyer,
  onSave,
}) {
  const [items, setItems] = useState([
    { sno: 1, description: "", hsn: "", quantity: "", rate: "", amount: "" },
  ]);
  const [taxPair, setTaxPair] = useState(TAX_OPTIONS[1]); // default 6+6
  const [roundOff, setRoundOff] = useState(0);
  const [invoiceNo, setInvoiceNo] = useState(header.invoiceNo || "");
  const [paymentType, setPaymentType] = useState("cash");
  const [paidAmount, setPaidAmount] = useState(0);
  const [balanceDue, setBalanceDue] = useState(0);

  // Calculations
  const subtotal = useMemo(() => {
    return items.reduce((sum, it) => {
      const qty = parseFloat(it.quantity) || 0;
      const rate = parseFloat(it.rate) || 0;
      const amount = it.amount !== "" ? parseFloat(it.amount) || 0 : qty * rate;
      return sum + amount;
    }, 0);
  }, [items]);

  const cgstAmt = useMemo(
    () => subtotal * (taxPair.cgst / 100),
    [subtotal, taxPair]
  );
  const sgstAmt = useMemo(
    () => subtotal * (taxPair.sgst / 100),
    [subtotal, taxPair]
  );
  const totalGST = useMemo(() => cgstAmt + sgstAmt, [cgstAmt, sgstAmt]);
  const grandTotal = useMemo(
    () =>
      Math.round((subtotal + totalGST + (parseFloat(roundOff) || 0)) * 100) /
      100,
    [subtotal, totalGST, roundOff]
  );

  // Update balance due whenever payment type, grand total, or paid amount changes
  useEffect(() => {
    if (paymentType === "cash" || paymentType === "online") {
      setPaidAmount(grandTotal);
      setBalanceDue(0);
    } else if (paymentType === "credit") {
      setBalanceDue(grandTotal - paidAmount);
    }
  }, [grandTotal, paidAmount, paymentType]);

  // Listen for print messages
  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data === "PRINT_DONE" || e.data === "PRINT_CANCEL") {
        onClose();
        resetItems();
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [onClose]);

  const resetItems = () => {
    setItems([
      { sno: 1, description: "", hsn: "", quantity: "", rate: "", amount: "" },
    ]);
    setInvoiceNo("");
    setPaidAmount(0);
    setBalanceDue(0);
    setRoundOff(0);
  };

  // Add / Remove / Edit rows
  const addRow = () =>
    setItems((prev) => [
      ...prev,
      {
        sno: prev.length + 1,
        description: "",
        hsn: "",
        quantity: "",
        rate: "",
        amount: "",
      },
    ]);

  const removeRow = (idx) =>
    setItems((prev) =>
      prev.filter((_, i) => i !== idx).map((x, i) => ({ ...x, sno: i + 1 }))
    );

  const handleItemChange = (idx, field, value) =>
    setItems((prev) =>
      prev.map((it, i) => (i === idx ? { ...it, [field]: value } : it))
    );

  const formatDateForDB = (dateObj) => {
    const d = new Date(dateObj);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const saveToServer = async () => {
    try {
      const payload = {
        header: {
          ...header,
          date: formatDateForDB(header.date),
          paymentType,
          paidAmount: paymentType === "credit" ? paidAmount : grandTotal,
          balanceDue: paymentType === "credit" ? balanceDue : 0,
        },
        items,
        subtotal,
        cgst_percent: taxPair.cgst,
        cgst_amount: cgstAmt,
        sgst_percent: taxPair.sgst,
        sgst_amount: sgstAmt,
        round_off: parseFloat(roundOff) || 0,
        grand_total: grandTotal,
      };

      const res = await axios.post(`${Api}bills/create.php`, payload);
      // const res = await axios.post(`${Api}proxy.php`, payload);

      if (res.data?.invoice_no) setInvoiceNo(res.data.invoice_no);
      return res.data;
    } catch (e) {
      console.error("Save error:", e);
      return null;
    }
  };

  const printBill = async () => {
    try {
      const savedData = await saveToServer();
      const invoice = savedData?.invoice_no || "NA";

      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(generateHTML(invoice));
      doc.close();

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        setTimeout(() => document.body.removeChild(iframe), 1000);
      };
    } catch (err) {
      console.error("Print failed:", err);
    }
  };
  // Convert YYYY-MM-DD to DD/MM/YYYY
  function formatDateForInvoice(dateStr) {
    if (!dateStr) return "-";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  }


  const ROWS_PER_PAGE = 11;

  /* ===============================
    PAGE SPLITTING
=============================== */

  function splitPages(items, paymentType) {
    const creditExtra = paymentType === "credit" ? 2 : 0;
    const effectiveRows = ROWS_PER_PAGE - creditExtra;

    let pages = [];
    for (let i = 0; i < items.length; i += effectiveRows) {
      pages.push(items.slice(i, i + effectiveRows));
    }

    return pages;
  }

  /* ===============================
    MAIN HTML GENERATOR
=============================== */

  const generateHTML = (invoiceNo) => {
    const pages = splitPages(items, paymentType);

    let html = `
<html>
<head>
<title>Invoice ${invoiceNo}</title>
<script src="https://cdn.tailwindcss.com"></script>

<style>
* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
@page { margin: 0; }
.page-break { page-break-after: always; }

body { margin: 0; padding: 0; }

.watermark {
  position: fixed;
  top: 48%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.06;
  width: 320px;
  z-index: 0;
  pointer-events: none;
}

/* PAGE LAYOUT FIX */
.page-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
}

</style>
</head>

<body class="font-sans text-[13px] text-gray-800">
<img src="/logo.jpg" class="watermark" />
`;

    pages.forEach((pageItems, pageIndex) => {
      const isLastPage = pageIndex === pages.length - 1;

      const isFirstPageFull =
        pageIndex === 0 && pageItems.length === ROWS_PER_PAGE;

      const emptyRowsCount = ROWS_PER_PAGE - pageItems.length;

      const emptyRows = Array(emptyRowsCount)
        .fill(
          `
      <tr>
        <td class="border px-1 py-1 text-center">&nbsp;</td>
        <td class="border px-1 py-1">&nbsp;</td>
        <td class="border px-1 py-1 text-center">&nbsp;</td>
        <td class="border px-1 py-1 text-center">&nbsp;</td>
        <td class="border px-1 py-1 text-right">&nbsp;</td>
        <td class="border px-1 py-1 text-right">&nbsp;</td>
      </tr>
    `
        )
        .join("");

      html += `
<div class="page-container">

  <div class="page-content">

    ${headerSection(invoiceNo)}

    <table class="w-full border mt-4 text-[13px] bg-white">
      <thead>
        <tr>
          <th class="border px-1 py-1">S.NO</th>
          <th class="border px-1 py-1">DESCRIPTION</th>
          <th class="border px-1 py-1">HSN</th>
          <th class="border px-1 py-1">QTY</th>
          <th class="border px-1 py-1">RATE</th>
          <th class="border px-1 py-1">AMOUNT</th>
        </tr>
      </thead>

      <tbody>
        ${pageItems
          .map((it, i) => {
            const sno = pageIndex * ROWS_PER_PAGE + i + 1;
            const qty = parseFloat(it.quantity) || 0;
            const rate = parseFloat(it.rate) || 0;
            const amt = it.amount !== "" ? parseFloat(it.amount) : qty * rate;

            return `
          <tr>
            <td class="border px-1 py-1 text-center">${sno}</td>
            <td class="border px-1 py-1">${it.description || ""}</td>
            <td class="border px-1 py-1 text-center">${it.hsn || ""}</td>
            <td class="border px-1 py-1 text-center">${qty}</td>
            <td class="border px-1 py-1 text-right">${rate.toFixed(2)}</td>
            <td class="border px-1 py-1 text-right">${amt.toFixed(2)}</td>
          </tr>
          `;
          })
          .join("")}

        ${emptyRows}
      </tbody>
    </table>

    ${
      isLastPage
        ? totalsSection()
        : isFirstPageFull
        ? bankDetailsOnlySection()
        : ""
    }

  </div> <!-- END page-content -->

  ${footerSection()}

</div> <!-- END page-container -->

${!isLastPage ? `<div class="page-break"></div>` : ""}
    `;
    });

    html += `</body></html>`;
    return html;
  };

  /* ===============================
    HEADER SECTION (EVERY PAGE)
=============================== */

  function headerSection(invoiceNo) {
    return `
<div class="border-b pb-2">

  <div class="flex justify-between items-center relative">
  
    <div class="absolute left-1/2 -translate-x-1/2 flex flex-col items-center mt-2">
  <p class="text-[13px] font-bold leading-none">உ</p>
  <p class="text-[12px] font-semibold mt-1">ஸ்ரீ பெரியாண்டவர் துனை</p>
</div>


    <p class="text-[13px] font-semibold whitespace-nowrap ml-auto mt-4 mr-4">
      GSTIN : <span class="font-normal">33JXGPS6359E1Z0</span>
    </p>
  </div>

  <div class="flex items-center gap-4 mt-1">
    <img src="/logo.jpg" class="h-24" />

    <div class="flex-1">
      <div class="bg-[#020617] text-white px-6 py-2 rounded flex justify-end ml-auto w-full">
      
        <h1 class="text-4xl font-extrabold tracking-wide">
          ஸ்ரீ முருகன் டிரேடர்ஸ்
        </h1>
      </div>

      <p class="text-[14px] mt-2 ml-16">
        143-B, அத்தியூர் மெயின் ரோடு, லட்சுமி மருத்துவமனை அருகில், பவாணி - 638 301.
      </p>
    </div>
  </div>
</div>

<div class="grid grid-cols-2 gap-3 mt-3">
  <div class="border p-2 leading-6">
    <p><b>INVOICE NO :</b> ${invoiceNo}</p>
    <p><b>TO :</b></p>
    <p>${header.buyerName || "-"}</p>
    <p>${header.buyerAddress || "-"}</p>
  </div>

  <div class="border p-2 leading-6">
    <p><b>DATE :</b> ${formatDateForInvoice(header.date)}</p>
    <p><b>PAYMENT TYPE :</b> ${
      paymentType
        ? paymentType.charAt(0).toUpperCase() + paymentType.slice(1)
        : "-"
    }</p>
    <p><b>TRANSPORT :</b> ${header.transport || "-"}</p>
    <p><b>NO OF BUNDLES :</b> ${header.bundles || "-"}</p>
    <p><b>DELIVERY PLACE :</b> ${header.deliveryPlace || "-"}</p>
    <p><b>LR NO :</b> ${header.llrNo || "-"}</p>
  </div>
</div>
`;
  }

  /* ===============================
    SPECIAL CASE — BANK DETAILS ONLY
    (WHEN FIRST PAGE HAS EXACTLY 11 ROWS)
=============================== */

function bankDetailsOnlySection() {
  return `
  <div class="mt-16 ml-4 text-[13px] leading-6">
    <p class="font-semibold">BANK DETAILS</p>
    <p>A/C NO : 42370716371</p>
    <p>IFSC : SBIN0000971</p>
    <p>BRANCH : BHAVANI</p>
  </div>
  `;
}


  /* ===============================
    TOTALS SECTION — ONLY LAST PAGE
=============================== */

  function totalsSection() {
    return `
<div class="flex justify-between mt-4">
  <div class="text-[13px] ml-4 leading-6">
    <p><b>E & O.E</b></p>
    <p><b>TOTAL AMOUNT IN WORDS :</b></p>
    <p>${amountInWords(grandTotal)} Only.</p>

    <p class="mt-6 font-semibold">BANK DETAILS</p>
    <p>A/C NO : 42370716371</p>
    <p>IFSC : SBIN0000971</p>
    <p>BRANCH : BHAVANI</p>
  </div>

  <div class="w-1/2 mr-4">
    <table class="w-full border text-[13px]">
      <tr><td class="border px-1 py-1">SUB TOTAL</td><td class="border px-1 py-1 text-right">${subtotal.toFixed(
        2
      )}</td></tr>
      <tr><td class="border px-1 py-1">CGST @ ${
        taxPair.cgst
      }%</td><td class="border px-1 py-1 text-right">${cgstAmt.toFixed(
      2
    )}</td></tr>
      <tr><td class="border px-1 py-1">SGST @ ${
        taxPair.sgst
      }%</td><td class="border px-1 py-1 text-right">${sgstAmt.toFixed(
      2
    )}</td></tr>
      <tr><td class="border px-1 py-1">TOTAL TAX</td><td class="border px-1 py-1 text-right">${(
        cgstAmt + sgstAmt
      ).toFixed(2)}</td></tr>
      <tr><td class="border px-1 py-1">ROUND OFF</td><td class="border px-1 py-1 text-right">${roundOff}</td></tr>

      <tr class="font-bold"><td class="border px-1 py-1">GRAND TOTAL</td>
      <td class="border px-1 py-1 text-right">${grandTotal.toFixed(2)}</td></tr>

      ${
        paymentType === "credit"
          ? `
      <tr><td class="border px-1 py-1">PAID AMOUNT</td><td class="border px-1 py-1 text-right">${paidAmount.toFixed(
        2
      )}</td></tr>
      <tr><td class="border px-1 py-1">BALANCE DUE</td><td class="border px-1 py-1 text-right">${balanceDue.toFixed(
        2
      )}</td></tr>
      `
          : ""
      }
    </table>
  </div>
</div>

<div class="flex justify-between mt-10 px-4 text-[14px]">
  <p><b>CHECKED BY</b></p>
  <p class="font-semibold">FOR SRI MURUGAN TRADERS</p>
</div>
`;
  }

  /* ===============================
    FOOTER (BOTTOM OF EVERY PAGE)
=============================== */

  function footerSection() {
    return `
<div class="footer bg-[#020617] text-white flex justify-between items-center w-full py-3 px-4 mt-4">
  <div>
    <p class="text-lg font-semibold tracking-wide">99408 32688 | 90801 99109</p>
    <p class="text-[13px] mt-1">srimurugantraders.smt26@gmail.com</p>
  </div>

  <div class="text-right">
    <p class="text-xl font-bold tracking-wide">வாழ்க! வளமுடன்!!</p>
  </div>
</div>
`;
  }

  if (!open) return null;

  return (
    <div className="bp-backdrop">
      <div className="bp-modal">
        <div className="bp-header">
          <div>Create Bill</div>
          <button onClick={onClose} className="bp-close">
            ×
          </button>
        </div>

        <div className="bp-body">
          <div className="bp-row">
            <button
              onClick={addRow}
              className="bp-btn"
              style={{ marginBottom: "10px" }}
            >
              + Add Row
            </button>
          </div>

          {/* <div className="bp-table-wrap"> */}
          <div
            className="bp-table-wrap"
            style={{
              maxHeight: items.length > 3 ? "220px" : "auto",
              overflowY: items.length > 3 ? "auto" : "visible",
              overflowX: "auto", // ✅ FIX: allow horizontal scroll
              whiteSpace: "nowrap", // ✅ FIX: prevent column wrap
              border: "1px solid #ddd",
            }}
          >
            <table className="bp-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Description</th>
                  <th>HSN</th>
                  <th>Quantity</th>
                  <th>Rate</th>
                  <th>Amount</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => {
                  const qty = parseFloat(it.quantity) || 0;
                  const rate = parseFloat(it.rate) || 0;
                  const auto = (qty * rate).toFixed(2);
                  const showAmount = it.amount !== "" ? it.amount : auto;
                  return (
                    <tr key={idx}>
                      <td style={{ width: 70 }}>
                        <input
                          value={it.sno}
                          onChange={(e) =>
                            handleItemChange(idx, "sno", e.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={it.description}
                          onChange={(e) =>
                            handleItemChange(idx, "description", e.target.value)
                          }
                        />
                      </td>
                      <td style={{ width: 120 }}>
                        <input
                          value={it.hsn}
                          onChange={(e) =>
                            handleItemChange(idx, "hsn", e.target.value)
                          }
                        />
                      </td>
                      <td style={{ width: 120 }}>
                        <input
                          type="number"
                          value={it.quantity}
                          onChange={(e) =>
                            handleItemChange(idx, "quantity", e.target.value)
                          }
                        />
                      </td>
                      <td style={{ width: 120 }}>
                        <input
                          type="number"
                          value={it.rate}
                          onChange={(e) =>
                            handleItemChange(idx, "rate", e.target.value)
                          }
                        />
                      </td>
                      <td style={{ width: 140 }}>
                        <input
                          type="number"
                          value={showAmount}
                          onChange={(e) =>
                            handleItemChange(idx, "amount", e.target.value)
                          }
                        />
                      </td>
                      <td style={{ width: 60 }}>
                        <button
                          className="bp-del"
                          onClick={() => removeRow(idx)}
                        >
                          🗑
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="bp-summary">
            <div>
              <label>CGST %:</label>
              <input
                type="number"
                value={taxPair.cgst}
                onChange={(e) =>
                  setTaxPair((prev) => ({
                    ...prev,
                    cgst: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div>
              <label>SGST %:</label>
              <input
                type="number"
                value={taxPair.sgst}
                onChange={(e) =>
                  setTaxPair((prev) => ({
                    ...prev,
                    sgst: parseFloat(e.target.value) || 0,
                  }))
                }
              />
            </div>
            <div>
              Subtotal: <b>{subtotal.toFixed(2)}</b>
            </div>
            <div>
              CGST: <b>{cgstAmt.toFixed(2)}</b>
            </div>
            <div>
              SGST: <b>{sgstAmt.toFixed(2)}</b>
            </div>
            <div>
              <label>Round Off:</label>
              <input
                type="number"
                value={roundOff}
                onChange={(e) => setRoundOff(e.target.value)}
              />
            </div>
            <div>
              Grand Total: <b>{grandTotal.toFixed(2)}</b>
            </div>
          </div>
        </div>

        <div className="bp-summary" style={{ marginTop: "15px" }}>
          <div>
            <label>Payment Type:</label>
            <select
              value={paymentType}
              onChange={(e) => setPaymentType(e.target.value)}
            >
              <option value="cash">Cash</option>
              <option value="credit">Credit</option>
              <option value="online">Online Payment</option>
            </select>
          </div>

          {paymentType === "credit" && (
            <>
              <div>
                <label>Paid Amount:</label>
                <input
                  type="number"
                  value={paidAmount}
                  onChange={(e) => setPaidAmount(Number(e.target.value))}
                />
              </div>
              <div>
                Balance Due: <b>{balanceDue.toFixed(2)}</b>
              </div>
            </>
          )}

          {(paymentType === "cash" || paymentType === "online") && (
            <div>
              Paid Amount: <b>{grandTotal.toFixed(2)}</b>
            </div>
          )}
        </div>

        <div className="bp-footer">
          <button className="bp-btn" onClick={printBill}>
            Save & Print
          </button>
        </div>
      </div>
    </div>
  );
}

// Number to words
function amountInWords(n) {
  const s = Math.round(n).toString();
  const a = [
    "",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
    "Eleven",
    "Twelve",
    "Thirteen",
    "Fourteen",
    "Fifteen",
    "Sixteen",
    "Seventeen",
    "Eighteen",
    "Nineteen",
  ];
  const b = [
    "",
    "",
    "Twenty",
    "Thirty",
    "Forty",
    "Fifty",
    "Sixty",
    "Seventy",
    "Eighty",
    "Ninety",
  ];
  const u = (num) =>
    num < 20
      ? a[num]
      : b[Math.floor(num / 10)] + (num % 10 ? " " + a[num % 10] : "");
  const h = (num) =>
    num ? a[Math.floor(num / 100)] + " Hundred " + u(num % 100) : "";
  const num = parseInt(s, 10);
  if (num === 0) return "Zero";
  let out = "";
  const crore = Math.floor(num / 10000000);
  const lakh = Math.floor((num % 10000000) / 100000);
  const thousand = Math.floor((num % 100000) / 1000);
  const hundred = num % 1000;
  if (crore) out += u(crore) + " Crore ";
  if (lakh) out += u(lakh) + " Lakh ";
  if (thousand) out += u(thousand) + " Thousand ";
  if (hundred) out += h(hundred);
  return out.replace(/\s+/g, " ").trim();
}
